import { useEffect, useState } from "react";
import { LogOut, Loader2, Inbox, Flag, Users, Building2, Eye, Handshake } from "lucide-react";
import API, { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/evgp/Logo";

const STATUS_LABELS = { nuovo: "Nuovo", contattato: "Contattato", accreditato: "Accreditato" };
const MODALITA_LABELS = { team_in_pista: "Team in Pista", delegazione: "Delegazione" };

export default function AdminPage() {
  const [user, setUser] = useState("loading");
  const [tab, setTab] = useState("adesioni");
  const [adesioni, setAdesioni] = useState([]);
  const [partenariati, setPartenariati] = useState([]);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const loadAll = async () => {
    try {
      const [a, p] = await Promise.all([API.get("/admin/adesioni"), API.get("/admin/partenariati")]);
      setAdesioni(a.data);
      setPartenariati(p.data);
    } catch (e) {
      if (e.response?.status === 401) setUser(null);
    }
  };

  useEffect(() => {
    API.get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        loadAll();
      })
      .catch(() => setUser(null));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { data } = await API.post("/auth/login", creds);
      setUser(data);
      loadAll();
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await API.post("/auth/logout");
    } finally {
      setUser(null);
      setAdesioni([]);
      setPartenariati([]);
    }
  };

  const updateAdesioneStatus = async (id, status) => {
    try {
      const { data } = await API.patch(`/admin/adesioni/${id}`, { status });
      setAdesioni((prev) => prev.map((a) => (a.id === id ? data : a)));
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const updatePartenariatoStatus = async (id, status) => {
    try {
      const { data } = await API.patch(`/admin/partenariati/${id}`, { status });
      setPartenariati((prev) => prev.map((p) => (p.id === id ? data : p)));
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (user === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-vg-black">
        <Loader2 className="h-8 w-8 animate-spin text-vg-green" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-vg-black px-5 py-16" data-testid="admin-login-view">
        <div className="w-full max-w-md border border-white/10 bg-vg-card p-8 sm:p-10">
          <Logo />
          <h1 className="mt-8 font-display text-3xl font-bold uppercase tracking-wide text-vg-white">Area Organizzatore</h1>
          <p className="mt-2 text-sm text-vg-muted">Accedi per leggere le richieste di adesione.</p>
          <form onSubmit={handleLogin} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                required
                value={creds.email}
                onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
                data-testid="admin-email-input"
                className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white outline-none transition-colors focus:border-vg-green"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                required
                value={creds.password}
                onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                data-testid="admin-password-input"
                className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white outline-none transition-colors focus:border-vg-green"
              />
            </div>
            {loginError && (
              <p className="rounded border border-vg-red/40 bg-vg-red/10 px-4 py-3 text-sm text-vg-red" data-testid="admin-login-error">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              data-testid="admin-login-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-vg-green px-6 py-3.5 font-display text-base font-semibold uppercase tracking-wider text-vg-black transition-all duration-200 hover:shadow-[0_0_28px_rgba(16,185,129,0.5)] disabled:opacity-60"
            >
              {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
              Accedi
            </button>
          </form>
        </div>
      </div>
    );
  }

  const nuovi = adesioni.filter((a) => a.status === "nuovo").length;
  const team = adesioni.filter((a) => a.modalita === "team_in_pista").length;
  const delegazioni = adesioni.filter((a) => a.modalita === "delegazione").length;
  const SYNC_LABELS = { true: "CoGeSTur OK", false: "In attesa sync" };

  const STATS = [
    { icon: Inbox, label: "Richieste totali", value: adesioni.length, tid: "stat-totale" },
    { icon: Eye, label: "Da gestire", value: nuovi, tid: "stat-nuove" },
    { icon: Flag, label: "Team in Pista", value: team, tid: "stat-team" },
    { icon: Users, label: "Delegazioni", value: delegazioni, tid: "stat-delegazioni" },
  ];

  return (
    <div className="min-h-screen bg-vg-black px-5 py-10 text-vg-white sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-vg-muted" data-testid="admin-user-email">
              {user.email}
            </span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-display text-sm font-semibold uppercase tracking-wider text-vg-white transition-colors hover:border-vg-red hover:text-vg-red"
            >
              <LogOut className="h-4 w-4" />
              Esci
            </button>
          </div>
        </div>

        <h1 className="mt-10 font-display text-4xl font-bold uppercase tracking-wide text-vg-white">Richieste di Adesione</h1>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="border border-white/10 bg-vg-card p-6" data-testid={s.tid}>
              <s.icon className="h-6 w-6 text-vg-green" />
              <p className="mt-4 font-mono text-4xl tabular-nums text-vg-white">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-muted">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex gap-2">
          {[
            { id: "adesioni", label: "Adesioni", count: adesioni.length, tid: "admin-tab-adesioni" },
            { id: "partenariati", label: "Partenariati", count: partenariati.length, tid: "admin-tab-partenariati" },
          ].map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={tb.tid}
              aria-pressed={tab === tb.id}
              className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-display text-sm font-semibold uppercase tracking-wider transition-all duration-200 ${
                tab === tb.id ? "border-vg-green bg-vg-green text-vg-black" : "border-white/15 text-vg-muted hover:border-vg-green/50 hover:text-vg-white"
              }`}
            >
              {tb.id === "partenariati" ? <Handshake className="h-4 w-4" /> : <Inbox className="h-4 w-4" />}
              {tb.label} ({tb.count})
            </button>
          ))}
        </div>

        {tab === "adesioni" ? (
          adesioni.length === 0 ? (
            <div className="mt-6 flex flex-col items-center border border-dashed border-white/15 py-20 text-center" data-testid="admin-empty-state">
              <Building2 className="h-10 w-10 text-vg-muted" />
              <p className="mt-4 font-display text-xl uppercase tracking-wider text-vg-muted">Nessuna richiesta ricevuta — ancora.</p>
              <p className="mt-2 max-w-sm text-sm text-vg-muted">
                Le schede di adesione inviate dal modulo sul sito compariranno qui in tempo reale.
              </p>
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto border border-white/10 bg-vg-card" data-testid="admin-adesioni-table">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">
                    <th className="px-5 py-4">Data</th>
                    <th className="px-5 py-4">Istituto</th>
                    <th className="px-5 py-4">Referente</th>
                    <th className="px-5 py-4">Modalità</th>
                    <th className="px-5 py-4">Contatti</th>
                    <th className="px-5 py-4">Messaggio</th>
                    <th className="px-5 py-4">Stato</th>
                    <th className="px-5 py-4">CoGeSTur</th>
                  </tr>
                </thead>
                <tbody>
                  {adesioni.map((a) => (
                    <tr key={a.id} className="border-b border-white/5 align-top transition-colors hover:bg-white/[0.02]" data-testid={`adesione-row-${a.id}`}>
                      <td className="px-5 py-4 font-mono text-xs text-vg-muted">
                        {new Date(a.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                      </td>
                      <td className="px-5 py-4 font-medium text-vg-white">{a.istituto}</td>
                      <td className="px-5 py-4 text-vg-muted">
                        {a.nome}
                        <br />
                        <span className="text-xs text-vg-muted/70">{a.ruolo}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] ${
                            a.modalita === "team_in_pista" ? "border-vg-green/50 text-vg-green" : "border-vg-red/50 text-vg-red"
                          }`}
                        >
                          {MODALITA_LABELS[a.modalita] || a.modalita}
                          {a.num_studenti ? ` · ${a.num_studenti} st.` : ""}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-vg-muted">
                        <a href={`mailto:${a.email}`} className="text-vg-green hover:underline">
                          {a.email}
                        </a>
                        {a.telefono && <br />}
                        {a.telefono}
                      </td>
                      <td className="max-w-[220px] px-5 py-4 text-xs text-vg-muted">{a.messaggio || "—"}</td>
                      <td className="px-5 py-4">
                        <select
                          value={a.status}
                          onChange={(e) => updateAdesioneStatus(a.id, e.target.value)}
                          data-testid={`adesione-status-${a.id}`}
                          className="border border-white/15 bg-vg-black px-3 py-2 font-mono text-xs text-vg-white outline-none focus:border-vg-green"
                        >
                          {Object.entries(STATUS_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>
                              {v}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-block rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] ${
                            a.cogestur_synced ? "border-vg-green/50 text-vg-green" : "border-amber-400/50 text-amber-400"
                          }`}
                          title={a.cogestur_id || ""}
                        >
                          {SYNC_LABELS[String(!!a.cogestur_synced)]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : partenariati.length === 0 ? (
          <div className="mt-6 flex flex-col items-center border border-dashed border-white/15 py-20 text-center" data-testid="admin-empty-partenariati">
            <Handshake className="h-10 w-10 text-vg-muted" />
            <p className="mt-4 font-display text-xl uppercase tracking-wider text-vg-muted">Nessuna richiesta di partenariato — ancora.</p>
            <p className="mt-2 max-w-sm text-sm text-vg-muted">
              Le richieste inviate dal modulo "Diventa Sponsor" compariranno qui in tempo reale.
            </p>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto border border-white/10 bg-vg-card" data-testid="admin-partenariati-table">
            <table className="w-full min-w-[800px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">
                  <th className="px-5 py-4">Data</th>
                  <th className="px-5 py-4">Azienda</th>
                  <th className="px-5 py-4">Referente</th>
                  <th className="px-5 py-4">Contatti</th>
                  <th className="px-5 py-4">Messaggio</th>
                  <th className="px-5 py-4">Stato</th>
                  <th className="px-5 py-4">CoGeSTur</th>
                </tr>
              </thead>
              <tbody>
                {partenariati.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 align-top transition-colors hover:bg-white/[0.02]" data-testid={`partenariato-row-${p.id}`}>
                    <td className="px-5 py-4 font-mono text-xs text-vg-muted">
                      {new Date(p.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })}
                    </td>
                    <td className="px-5 py-4 font-medium text-vg-white">{p.azienda}</td>
                    <td className="px-5 py-4 text-vg-muted">{p.referente}</td>
                    <td className="px-5 py-4 text-xs text-vg-muted">
                      <a href={`mailto:${p.email}`} className="text-vg-green hover:underline">
                        {p.email}
                      </a>
                      {p.telefono && <br />}
                      {p.telefono}
                    </td>
                    <td className="max-w-[220px] px-5 py-4 text-xs text-vg-muted">{p.messaggio || "—"}</td>
                    <td className="px-5 py-4">
                      <select
                        value={p.status}
                        onChange={(e) => updatePartenariatoStatus(p.id, e.target.value)}
                        data-testid={`partenariato-status-${p.id}`}
                        className="border border-white/15 bg-vg-black px-3 py-2 font-mono text-xs text-vg-white outline-none focus:border-vg-green"
                      >
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-block rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] ${
                          p.cogestur_synced ? "border-vg-green/50 text-vg-green" : "border-amber-400/50 text-amber-400"
                        }`}
                        title={p.cogestur_id || ""}
                      >
                        {SYNC_LABELS[String(!!p.cogestur_synced)]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
